/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo, useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { bind } from 'bind-event-listener';
import { createAndFireEvent, withAnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import noop from '@atlaskit/ds-lib/noop';
import { UNSAFE_LAYERING, useCloseOnEscapePress } from '@atlaskit/layering';
import { fg } from '@atlaskit/platform-feature-flags';
import { Manager, Popper, Reference } from '@atlaskit/popper';
import NodeResolverWrapper from './node-resolver-wrapper';
import { Container } from './styled/container';
var packageName = "@atlaskit/inline-dialog";
var packageVersion = "14.3.1";
var checkIsChildOfPortal = function checkIsChildOfPortal(node) {
  if (!node) {
    return false;
  }
  return node.classList && node.classList.contains('atlaskit-portal-container') || checkIsChildOfPortal(node.parentElement);
};

// escape close manager for layering
var EscapeCloseManager = function EscapeCloseManager(_ref) {
  var handleClose = _ref.handleClose;
  useCloseOnEscapePress({
    onClose: handleClose
  });
  // only create a dummy component for using ths hook in class component
  return jsx("span", null);
};
var InlineDialogComponent = /*#__PURE__*/memo(function InlineDialog(_ref2) {
  var _ref2$isOpen = _ref2.isOpen,
    isOpen = _ref2$isOpen === void 0 ? false : _ref2$isOpen,
    _ref2$onContentBlur = _ref2.onContentBlur,
    onContentBlur = _ref2$onContentBlur === void 0 ? noop : _ref2$onContentBlur,
    _ref2$onContentClick = _ref2.onContentClick,
    onContentClick = _ref2$onContentClick === void 0 ? noop : _ref2$onContentClick,
    _ref2$onContentFocus = _ref2.onContentFocus,
    onContentFocus = _ref2$onContentFocus === void 0 ? noop : _ref2$onContentFocus,
    _ref2$onClose = _ref2.onClose,
    onClose = _ref2$onClose === void 0 ? noop : _ref2$onClose,
    _ref2$placement = _ref2.placement,
    placement = _ref2$placement === void 0 ? 'bottom-start' : _ref2$placement,
    _ref2$strategy = _ref2.strategy,
    strategy = _ref2$strategy === void 0 ? 'fixed' : _ref2$strategy,
    testId = _ref2.testId,
    content = _ref2.content,
    children = _ref2.children;
  var containerRef = useRef(null);
  var triggerRef = useRef(null);
  // we put this into a ref to avoid handleCloseRequest having this as a dependency
  var onCloseRef = useRef(onClose);
  var isLayeringEnabled = fg('platform.design-system-team.inline-message-layering_wfp1p') && isOpen;
  useEffect(function () {
    onCloseRef.current = onClose;
  });
  var handleCloseRequest = useCallback(function (event) {
    var target = event.target;
    // checks for when target is not HTMLElement
    if (!(target instanceof HTMLElement)) {
      return;
    }

    // TODO: This is to handle the case where the target is no longer in the DOM.
    // This happens with react-select in datetime picker. There might be other
    // edge cases for this.
    if (!document.body.contains(target)) {
      return;
    }
    if (isLayeringEnabled) {
      var _onCloseRef$current;
      (_onCloseRef$current = onCloseRef.current) === null || _onCloseRef$current === void 0 || _onCloseRef$current.call(onCloseRef, {
        isOpen: false,
        event: event
      });
      return;
    }

    // handles the case where inline dialog opens portalled elements such as modal
    if (checkIsChildOfPortal(target)) {
      return;
    }

    // call onClose if the click originated from outside the dialog
    if (containerRef.current && !containerRef.current.contains(target)) {
      var _onCloseRef$current2;
      (_onCloseRef$current2 = onCloseRef.current) === null || _onCloseRef$current2 === void 0 || _onCloseRef$current2.call(onCloseRef, {
        isOpen: false,
        event: event
      });
    }
  }, [isLayeringEnabled]);
  var handleClick = useCallback(function (event) {
    var _containerRef$current;
    // exit if we click outside but on the trigger — it can handle the clicks itself
    if (triggerRef.current && triggerRef.current.contains(event.target)) {
      return;
    }

    // if feature flag is enabled we won't file the close event when clicking inside dialog
    if (isLayeringEnabled && (_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(event.target)) {
      return;
    }
    handleCloseRequest(event);
  }, [handleCloseRequest, isLayeringEnabled]);
  useEffect(function () {
    if (!isOpen) {
      return;
    }
    return bind(window, {
      type: 'click',
      listener: handleClick,
      options: {
        capture: true
      }
    });
  }, [isOpen, handleClick]);
  var handleKeyDown = useCallback(function (event) {
    if (event.key === 'Escape') {
      handleCloseRequest(event);
    }
  }, [handleCloseRequest]);
  useEffect(function () {
    // if layering is enabled, we will use useCloseOnEscapePress hook instead
    if (!isOpen || isLayeringEnabled) {
      return;
    }
    var unbind = bind(window, {
      type: 'keydown',
      listener: handleKeyDown,
      options: {
        capture: true
      }
    });
    return unbind;
  }, [isOpen, handleKeyDown, isLayeringEnabled]);
  var popper = isOpen ? jsx(Popper, {
    placement: placement,
    strategy: strategy
  }, function (_ref3) {
    var _ref4 = _ref3.ref,
      style = _ref3.style;
    return jsx(Container, {
      onBlur: onContentBlur,
      onFocus: onContentFocus,
      onClick: onContentClick,
      ref: function ref(node) {
        if (node) {
          containerRef.current = node;
          if (typeof _ref4 === 'function') {
            _ref4(node);
          } else {
            _ref4.current = node;
          }
        }
      }
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      ,
      style: style,
      testId: testId
    }, content);
  }) : null;
  return jsx(Manager, null, jsx(Reference, null, function (_ref5) {
    var ref = _ref5.ref;
    return jsx(NodeResolverWrapper, {
      innerRef: function innerRef(node) {
        triggerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
        // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
      },
      hasNodeResolver: !fg('dsp-19516-design-system-portal-logic-update')
    }, jsx(React.Fragment, null, children));
  }), isLayeringEnabled ? jsx(UNSAFE_LAYERING, {
    isDisabled: false
  }, popper, jsx(EscapeCloseManager, {
    handleClose: handleCloseRequest
  })) : popper);
});
InlineDialogComponent.displayName = 'InlineDialog';
export { InlineDialogComponent as InlineDialogWithoutAnalytics };
var createAndFireEventOnAtlaskit = createAndFireEvent('atlaskit');

/**
 * __Inline dialog__
 *
 * _We are planning on deprecating Inline dialog. We recommend using the Popup component instead._
 *
 * An inline dialog is a pop-up container for small amounts of information. It can also contain controls.
 *
 * - [Examples](https://atlassian.design/components/inline-dialog/examples)
 * - [Code](https://atlassian.design/components/inline-dialog/code)
 * - [Usage](https://atlassian.design/components/inline-dialog/usage)
 */
var InlineDialog = withAnalyticsContext({
  componentName: 'inlineDialog',
  packageName: packageName,
  packageVersion: packageVersion
})(withAnalyticsEvents({
  onClose: createAndFireEventOnAtlaskit({
    action: 'closed',
    actionSubject: 'inlineDialog',
    attributes: {
      componentName: 'inlineDialog',
      packageName: packageName,
      packageVersion: packageVersion
    }
  })
})(InlineDialogComponent));
export default InlineDialog;